import { NS, type NamespaceTuple } from '@ownyou/shared-types';
import type { DataSourceId } from '../../contexts/DataSourceContext';

export const WELCOME_OUTCOME_SCHEMA_VERSION = 1;

export type WelcomeOutcomeState =
  | 'started'
  | 'completed'
  | 'skipped'
  | 'user_skipped_sources'
  | 'denied'
  | 'failed'
  | 'blocked'
  | 'timed_out'
  | 'ready'
  | 'building';

export interface WelcomeOutcomeRecord {
  schemaVersion: 1;
  walletId: string;
  userId?: string;
  state: WelcomeOutcomeState;
  reasonCode: string;
  sourceId?: DataSourceId;
  routeId: string;
  nextRoute?: string;
  evidenceRefs?: readonly string[];
  createdAt: number;
  updatedAt: number;
}

interface WelcomeOutcomeStoreLike {
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
}

const FORBIDDEN_VALUE_PATTERNS = [
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /client[_-]?secret/i,
  /authorization/i,
  /bearer\s+/i,
  /raw[_-]?(body|content|response)/i,
  /prompt/i,
  /credential/i,
  /private[_-]?key/i,
] as const;

export function welcomeOutcomeNamespace(userId: string): NamespaceTuple {
  return NS.welcomeOutcomes(userId);
}

export function validateWelcomeOutcomeBodySafety(record: WelcomeOutcomeRecord): string[] {
  const serialized = JSON.stringify(record);
  return FORBIDDEN_VALUE_PATTERNS
    .filter(pattern => pattern.test(serialized))
    .map(pattern => pattern.source);
}

export function createWelcomeOutcomeRecord(input: {
  walletId: string;
  userId?: string;
  state: WelcomeOutcomeState;
  reasonCode: string;
  sourceId?: DataSourceId;
  routeId?: string;
  nextRoute?: string;
  evidenceRefs?: readonly string[];
  now?: number;
}): WelcomeOutcomeRecord {
  const now = input.now ?? Date.now();
  const record: WelcomeOutcomeRecord = {
    schemaVersion: WELCOME_OUTCOME_SCHEMA_VERSION,
    walletId: input.walletId,
    ...(input.userId ? { userId: input.userId } : {}),
    state: input.state,
    reasonCode: input.reasonCode,
    ...(input.sourceId ? { sourceId: input.sourceId } : {}),
    routeId: input.routeId ?? 'welcome',
    ...(input.nextRoute ? { nextRoute: input.nextRoute } : {}),
    ...(input.evidenceRefs ? { evidenceRefs: input.evidenceRefs } : {}),
    createdAt: now,
    updatedAt: now,
  };

  const violations = validateWelcomeOutcomeBodySafety(record);
  if (violations.length > 0) {
    throw new Error(`WelcomeOutcome body-safety violation: ${violations.join(', ')}`);
  }

  return record;
}

export async function writeWelcomeOutcome(input: {
  store: WelcomeOutcomeStoreLike;
  walletId: string;
  userId?: string;
  state: WelcomeOutcomeState;
  reasonCode: string;
  sourceId?: DataSourceId;
  routeId?: string;
  nextRoute?: string;
  evidenceRefs?: readonly string[];
  now?: number;
}): Promise<WelcomeOutcomeRecord> {
  const record = createWelcomeOutcomeRecord(input);

  await input.store.put(
    welcomeOutcomeNamespace(input.userId ?? input.walletId),
    `${record.createdAt}-${record.state}`,
    record,
  );

  return record;
}
